import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { toast } from "sonner";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  FileSignature,
  Plus,
  Mail,
  Loader2,
  Download,
  Eye,
  Trash2,
  Copy,
  CheckCircle2,
  Clock,
  Bell,
} from "lucide-react";

const fmt = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
};

const fmtRelative = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const diffMin = Math.round((Date.now() - d.getTime()) / 60000);
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffMin < 1440) return `${Math.round(diffMin / 60)}h ago`;
  return `${Math.round(diffMin / 1440)}d ago`;
};

const W9Requests = () => {
  const { isAdmin } = useAuth();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({
    vendor_name: "",
    vendor_email: "",
    client_company_name: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    try {
      const res = await api.get("/w9/requests");
      setRequests(res.data);
    } catch {
      toast.error("Failed to load requests");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.vendor_name || !form.vendor_email || !form.client_company_name) {
      toast.error("All fields are required");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/w9/requests", form);
      toast.success(`W-9 request sent to ${form.vendor_email}`);
      setDialogOpen(false);
      setForm({ vendor_name: "", vendor_email: "", client_company_name: "" });
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send");
    } finally {
      setSubmitting(false);
    }
  };

  const remind = async (req) => {
    try {
      await api.post(`/w9/requests/${req.id}/remind`);
      toast.success(`Reminder sent to ${req.vendor_email}`);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send reminder");
    }
  };

  const remove = async (req) => {
    if (!window.confirm(`Delete W-9 request for ${req.vendor_name}?`)) return;
    try {
      await api.delete(`/w9/requests/${req.id}`);
      toast.success("Deleted");
      await load();
    } catch {
      toast.error("Failed to delete");
    }
  };

  const downloadPdf = async (req) => {
    try {
      const res = await api.get(`/w9/requests/${req.id}/pdf`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const safeName = req.vendor_name.replace(/[^a-z0-9]/gi, "_");
      a.download = `W9_${safeName}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to download");
    }
  };

  const viewPdf = async (req) => {
    try {
      const res = await api.get(`/w9/requests/${req.id}/pdf?inline=1`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const win = window.open(url, "_blank", "noopener,noreferrer");
      if (!win) {
        toast.error("Pop-up blocked — allow pop-ups for this site to view inline.");
      }
      // Revoke after the new tab has a chance to load the blob.
      setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to open");
    }
  };

  const copyLink = (req) => {
    const url = `${window.location.origin}/w9-form/${req.token}`;
    navigator.clipboard.writeText(url);
    toast.success("Form link copied to clipboard");
  };

  const pending = requests.filter((r) => r.status === "pending");
  const completed = requests.filter((r) => r.status === "completed");

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="w9-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-indigo-500/10 text-indigo-500 flex items-center justify-center">
            <FileSignature size={24} />
          </div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-foreground tracking-tight">
              W-9 Requests
            </h1>
            <p className="text-sm text-muted-foreground">
              {isAdmin
                ? "Send & track W-9 requests across the firm"
                : "Send & track your W-9 requests"}
            </p>
          </div>
        </div>
        <Button
          onClick={() => setDialogOpen(true)}
          data-testid="w9-new-btn"
          className="bg-indigo-600 hover:bg-indigo-700"
        >
          <Plus size={16} className="mr-1.5" />
          New W-9 Request
        </Button>
      </div>

      {/* Pending */}
      <div>
        <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-3 flex items-center gap-2">
          <Clock size={14} />
          Pending ({pending.length})
        </h2>
        {pending.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center text-sm text-muted-foreground">
              No pending W-9 requests
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {pending.map((r) => (
              <Card key={r.id} data-testid={`w9-pending-${r.id}`}>
                <CardContent className="p-4 space-y-2">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-foreground truncate">
                        {r.vendor_name}
                      </p>
                      <p className="text-xs text-muted-foreground truncate">
                        {r.vendor_email}
                      </p>
                    </div>
                    <Badge className="bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/30">
                      Pending
                    </Badge>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    For client: <span className="text-foreground font-medium">{r.client_company_name}</span>
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Sent {fmt(r.created_at)} · by {r.requester_name}
                    {r.reminder_count > 0 && ` · ${r.reminder_count} reminder${r.reminder_count > 1 ? "s" : ""} sent${r.last_reminder_at ? " (" + fmtRelative(r.last_reminder_at) + ")" : ""}`}
                  </p>
                  <div className="flex flex-wrap gap-1.5 pt-1">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => remind(r)}
                      data-testid={`w9-remind-${r.id}`}
                    >
                      <Bell size={13} className="mr-1" />
                      Send Reminder
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => copyLink(r)}
                    >
                      <Copy size={13} className="mr-1" />
                      Copy Link
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-destructive hover:text-destructive"
                      onClick={() => remove(r)}
                    >
                      <Trash2 size={13} />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Completed */}
      <div>
        <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-3 flex items-center gap-2">
          <CheckCircle2 size={14} />
          Completed ({completed.length})
          <span className="text-xs font-normal normal-case text-muted-foreground/70">
            · last 10 retained firm-wide
          </span>
        </h2>
        {completed.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center text-sm text-muted-foreground">
              No completed W-9s yet
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {completed.map((r) => (
              <Card key={r.id} data-testid={`w9-completed-${r.id}`}>
                <CardContent className="p-4 space-y-2">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-foreground truncate">
                        {r.legal_name || r.vendor_name}
                      </p>
                      {r.business_name && (
                        <p className="text-xs text-muted-foreground truncate">
                          {r.business_name}
                        </p>
                      )}
                      {r.tin_masked && (
                        <p className="text-xs text-muted-foreground font-mono">
                          TIN: {r.tin_masked}
                        </p>
                      )}
                    </div>
                    <Badge className="bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/30">
                      <CheckCircle2 size={12} className="mr-1" />
                      Completed
                    </Badge>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    For client: <span className="text-foreground font-medium">{r.client_company_name}</span>
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Completed {fmt(r.completed_at)} · requested by {r.requester_name}
                  </p>
                  <div className="flex flex-wrap gap-1.5 pt-1">
                    <Button
                      size="sm"
                      onClick={() => viewPdf(r)}
                      data-testid={`w9-view-${r.id}`}
                    >
                      <Eye size={13} className="mr-1" />
                      View
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => downloadPdf(r)}
                      data-testid={`w9-download-${r.id}`}
                    >
                      <Download size={13} className="mr-1" />
                      Download
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-destructive hover:text-destructive"
                      onClick={() => remove(r)}
                    >
                      <Trash2 size={13} />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* New Request dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="w9-new-dialog">
          <DialogHeader>
            <DialogTitle>New W-9 Request</DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Vendor / Contractor Name *
              </label>
              <Input
                value={form.vendor_name}
                onChange={(e) => setForm({ ...form, vendor_name: e.target.value })}
                placeholder="ABC Plumbing LLC"
                data-testid="w9-form-vendor-name"
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Vendor Email *
              </label>
              <Input
                type="email"
                value={form.vendor_email}
                onChange={(e) => setForm({ ...form, vendor_email: e.target.value })}
                placeholder="vendor@example.com"
                data-testid="w9-form-vendor-email"
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Requesting on behalf of (Client Company) *
              </label>
              <Input
                value={form.client_company_name}
                onChange={(e) =>
                  setForm({ ...form, client_company_name: e.target.value })
                }
                placeholder="Smith Construction Inc."
                data-testid="w9-form-client"
              />
              <p className="text-xs text-muted-foreground">
                Vendor will see this so they know which of your clients is asking.
              </p>
            </div>
            <div className="bg-blue-500/5 border border-blue-500/20 rounded-lg p-3 text-xs text-muted-foreground flex gap-2">
              <Mail size={14} className="shrink-0 mt-0.5 text-blue-500" />
              An email with a secure link will be sent to the vendor immediately.
              You&apos;ll be notified by email when they complete the W-9.
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={submitting}
                className="bg-indigo-600 hover:bg-indigo-700"
                data-testid="w9-form-submit"
              >
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Sending…
                  </>
                ) : (
                  "Send Request"
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default W9Requests;
